import { MAX_HEIGHT_LENGTH, MAX_NAME_LENGTH } from '@/validation';
import React, { type FC } from 'react';
import { TouchableOpacity } from 'react-native';

import { avatarDefault } from '@/assets/images';
import { Stack } from '@/components/layout';
import { Avatar, FormDateInput, FormInput, FormSelectInput, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { useAvatarPicker } from '@/hooks/use-avatar-picker';
import { colors } from '@/theme/colors';
import { Controller, useFormContext } from 'react-hook-form';
import type { MutatePlayerForm, MutatePlayerOption } from './types';

type MutatePlayerProfileSectionProps = {
  genderOptions: MutatePlayerOption[];
  genderPlaceholder?: string;
  levelOptions: MutatePlayerOption[];
  hideLevelField?: boolean;
};

export const MutatePlayerProfileSection: FC<MutatePlayerProfileSectionProps> = ({
  genderOptions,
  genderPlaceholder = STRINGS.player.mutatePlayer.profile.playerGenderPlaceholder,
  levelOptions,
  hideLevelField = false,
}) => {
  const { scale, isTablet } = useScaling();
  const rowGap = scale(isTablet ? 12 : 18);
  const styles = useStyles();
  const { control, setValue } = useFormContext<MutatePlayerForm>();
  const pickAvatar = useAvatarPicker();

  const handlePickAvatar = () => {
    pickAvatar(value => {
      setValue('avatar', value, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    });
  };

  return (
    <Stack gap={scale(12)} width="100%">
      <Stack direction="row" align="center" gap={scale(10)}>
        <Controller
          name="avatar"
          control={control}
          render={({ field: { value }, fieldState: { error } }) => (
            <>
              <Avatar
                onPress={handlePickAvatar}
                size={scale(60)}
                borderColor={error ? colors.Error : 'transparent'}>
                <Avatar.Image source={value?.uri ?? avatarDefault} />
              </Avatar>
              <TouchableOpacity
                activeOpacity={0.6}
                style={styles.uploadContainer}
                onPress={handlePickAvatar}>
                <Stack gap={scale(2)}>
                  <Stack direction="row" align="center" gap={scale(4)}>
                    <Text
                      variant={
                        isTablet ? 'Font Inter/Subtitle/Medium 12px' : 'Font Inter/Subtitle/Medium'
                      }
                      style={styles.uploadText}>
                      {STRINGS.player.mutatePlayer.profile.uploadPhoto}
                    </Text>
                    <SvgIcon
                      name="IcPaginationArrow"
                      size={scale(16)}
                      color={colors.Blue}
                      style={{ marginBottom: scale(2) }}
                    />
                  </Stack>
                  <Controller
                    name="avatar.fileSize"
                    control={control}
                    render={({ fieldState: { error: fileSizeError } }) => (
                      <Text
                        variant="Font Inter/Subtitle/Italic"
                        fontStyle="italic"
                        style={
                          fileSizeError?.message ? { color: colors.Error } : { color: colors['6'] }
                        }>
                        {fileSizeError?.message
                          ? fileSizeError?.message
                          : STRINGS.player.mutatePlayer.profile.uploadPhotoHint}
                      </Text>
                    )}
                  />
                </Stack>
              </TouchableOpacity>
            </>
          )}
        />
      </Stack>

      <Stack direction={isTablet ? 'row' : 'column'} gap={rowGap} width="100%">
        <Stack flex={isTablet ? 1 : undefined} minWidth={0}>
          <Controller
            name="firstName"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <FormInput
                label={STRINGS.player.mutatePlayer.profile.firstName}
                value={value}
                trim
                required
                status={error?.message ? 'error' : 'default'}
                errorMessage={error?.message}
                maxLength={MAX_NAME_LENGTH}
                onBlur={onBlur}
                onChangeText={firstName => onChange(firstName)}
                placeholder={STRINGS.player.mutatePlayer.profile.firstName}
              />
            )}
          />
        </Stack>
        <Stack flex={isTablet ? 1 : undefined} minWidth={0}>
          <Controller
            name="lastName"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <FormInput
                label={STRINGS.player.mutatePlayer.profile.lastName}
                required
                value={value}
                trim
                status={error?.message ? 'error' : 'default'}
                errorMessage={error?.message}
                maxLength={MAX_NAME_LENGTH}
                onBlur={onBlur}
                onChangeText={lastName => onChange(lastName)}
                placeholder={STRINGS.player.mutatePlayer.profile.lastName}
              />
            )}
          />
        </Stack>
      </Stack>

      {isTablet ? (
        <Stack direction="row" gap={rowGap} width="100%" align="flex-start">
          <Stack flex={1} minWidth={0}>
            <Controller
              name="birthDate"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <FormDateInput
                  label={STRINGS.player.mutatePlayer.profile.birthDate}
                  required
                  value={value}
                  errorMessage={error?.message}
                  onDateChange={birthDate => onChange(birthDate)}
                  defaultValue={new Date()}
                  maximumDate={new Date()}
                  placeholder={STRINGS.player.mutatePlayer.profile.birthDatePlaceholder}
                  status={error?.message ? 'error' : 'default'}
                />
              )}
            />
          </Stack>
          <Stack flex={1} minWidth={0}>
            <Controller
              name="playerHeight"
              control={control}
              render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <FormInput
                  label={STRINGS.player.mutatePlayer.profile.playerHeight}
                  required={false}
                  value={value}
                  onBlur={() => {
                    onChange(formatPlayerHeightInchesDisplay(value));
                    onBlur();
                  }}
                  trim
                  status={error?.message ? 'error' : 'default'}
                  errorMessage={error?.message}
                  keyboardType="numeric"
                  maxLength={MAX_HEIGHT_LENGTH}
                  onFocus={() => {
                    onChange(value.replace(/[^0-9]/g, ''));
                  }}
                  onChangeText={playerHeight =>
                    playerHeight.length < 3 && onChange(playerHeight.replace(/[^0-9]/g, ''))
                  }
                  placeholder={STRINGS.player.mutatePlayer.profile.playerHeightPlaceholder}
                />
              )}
            />
          </Stack>
          <Stack flex={1} minWidth={0}>
            <Controller
              name="gender"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <FormSelectInput
                  label={STRINGS.player.mutatePlayer.profile.playerGender}
                  required
                  options={genderOptions}
                  value={value}
                  onValueChange={gender => onChange(gender)}
                  placeholder={genderPlaceholder}
                  status={error?.message ? 'error' : 'default'}
                  errorMessage={error?.message}
                />
              )}
            />
          </Stack>
          <Stack flex={1} minWidth={0}>
            {hideLevelField ? null : (
              <Controller
                name="level"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <FormSelectInput
                    label={STRINGS.player.mutatePlayer.profile.level}
                    required={false}
                    options={levelOptions}
                    value={value}
                    onValueChange={level => onChange(level)}
                    placeholder={STRINGS.player.mutatePlayer.profile.levelPlaceholder}
                    status={error?.message ? 'error' : 'default'}
                    errorMessage={error?.message}
                  />
                )}
              />
            )}
          </Stack>
        </Stack>
      ) : (
        <>
          <Stack direction="row" gap={rowGap} width="100%">
            <Stack flex={1}>
              <Controller
                name="birthDate"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <FormDateInput
                    label={STRINGS.player.mutatePlayer.profile.birthDate}
                    value={value}
                    errorMessage={error?.message}
                    required
                    onDateChange={birthDate => onChange(birthDate)}
                    defaultValue={new Date()}
                    maximumDate={new Date()}
                    placeholder={STRINGS.player.mutatePlayer.profile.birthDatePlaceholder}
                    status={error?.message ? 'error' : 'default'}
                  />
                )}
              />
            </Stack>
            <Stack flex={1}>
              <Controller
                name="playerHeight"
                control={control}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <FormInput
                    label={STRINGS.player.mutatePlayer.profile.playerHeight}
                    required={false}
                    value={value}
                    onBlur={() => {
                      onChange(formatPlayerHeightInchesDisplay(value));
                      onBlur();
                    }}
                    status={error?.message ? 'error' : 'default'}
                    errorMessage={error?.message}
                    keyboardType="numeric"
                    maxLength={MAX_HEIGHT_LENGTH}
                    onFocus={() => {
                      onChange(value.replace(/[^0-9]/g, ''));
                    }}
                    onChangeText={playerHeight =>
                      playerHeight.length < 3 && onChange(playerHeight.replace(/[^0-9]/g, ''))
                    }
                    placeholder={STRINGS.player.mutatePlayer.profile.playerHeightPlaceholder}
                  />
                )}
              />
            </Stack>
          </Stack>
          <Stack direction="row" gap={rowGap} width="100%">
            <Stack flex={1}>
              <Controller
                name="gender"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <FormSelectInput
                    label={STRINGS.player.mutatePlayer.profile.playerGender}
                    required
                    options={genderOptions}
                    value={value}
                    onValueChange={gender => onChange(gender)}
                    placeholder={genderPlaceholder}
                    status={error?.message ? 'error' : 'default'}
                    errorMessage={error?.message}
                  />
                )}
              />
            </Stack>
            <Stack flex={1}>
              {hideLevelField ? null : (
                <Controller
                  name="level"
                  control={control}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <FormSelectInput
                      label={STRINGS.player.mutatePlayer.profile.level}
                      required={false}
                      options={levelOptions}
                      value={value}
                      onValueChange={level => onChange(level)}
                      placeholder={STRINGS.player.mutatePlayer.profile.levelPlaceholder}
                      status={error?.message ? 'error' : 'default'}
                      errorMessage={error?.message}
                    />
                  )}
                />
              )}
            </Stack>
          </Stack>
        </>
      )}
    </Stack>
  );
};

function formatPlayerHeightInchesDisplay(raw: string | undefined): string {
  const digits = (raw ?? '').replace(/\D/g, '').slice(0, 2);
  if (!digits) return '';
  return `${digits}"`;
}

const useStyles = createStyles(() => ({
  uploadText: {
    color: colors.Blue,
    textTransform: 'uppercase',
  },
  uploadHint: {
    color: colors['6'],
  },
  uploadArrow: {
    transform: [{ rotate: '180deg' }],
  },
  uploadContainer: {
    flex: 1,
  },
}));
